import { AppTranslationPipe } from '@adapter/i18n/app-translation.pipe';
import {
  ChangeDetectionStrategy,
  Component,
  forwardRef,
  input,
  output,
  signal
} from '@angular/core';
import {
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule
} from '@angular/forms';
import { AppInputOption } from '@helpers/input/input.option';
import { FloatLabelModule } from 'primeng/floatlabel';
import { SelectModule } from 'primeng/select';
/**
 * Componente que renderiza un input de tipo dropdown
 */
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-input-dropdown',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    SelectModule,
    FloatLabelModule,
    AppTranslationPipe
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AppInputDropdownComponent),
      multi: true
    }
  ],
  template: `
    <p-floatLabel>
      <p-select
        [inputId]="id()"
        [options]="items()"
        optionLabel="description"
        [showClear]="true"
        [checkmark]="true"
        [loading]="loading()"
        [filter]="true"
        filterBy="description"
        styleClass="w-full"
        appendTo="body"
        [disabled]="isDisabled()"
        (onChange)="onItemChange()"
        [(ngModel)]="value"
        (ngModelChange)="updateValue($event)"
      />
      <label for="{{ id() }}">{{ label() | translate }}</label>
    </p-floatLabel>
  `,
  styles: ``
})
export class AppInputDropdownComponent implements ControlValueAccessor {
  /**
   * Identificador del input
   */
  id = input.required<string>();

  /**
   * Opciones del dropdown
   */
  items = input.required<AppInputOption[]>();

  /**
   * Placeholder del input
   */
  label = input.required<string>();

  /**
   * Indica si el input está cargando
   */
  loading = input<boolean>(false);

  /**
   * Evento que se emite cuando el valor del input cambia
   */
  changed = output<AppInputOption | undefined>();

  /**
   * Valor del input
   */
  value = signal<AppInputOption | undefined>(undefined);

  /**
   * Indica si el input está deshabilitado
   */
  isDisabled = signal(false);

  onChange?: (value: AppInputOption) => void;

  onTouched?: () => void;

  onItemChange(): void {
    this.changed.emit(this.value());
  }

  writeValue(value: AppInputOption): void {
    this.value.set(value);
  }

  registerOnChange(fn: (value: AppInputOption) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled.set(isDisabled);
  }

  updateValue(event: AppInputOption): void {
    this.value.set(event);
    this.onChange!(event);
    this.onTouched!();
  }
}
